<template>
  <div clasS="bg-neutral-100 pt-[72px] -mt-[72px] lg:pt-28 lg:-mt-24 pb-5 lg:pb-28">
    <article class="container grid-default">
      <div clasS="gap-pull col-span-6 lg:col-span-12 lg:rounded-3xl">
        <Image
          class="aspect-cinema lg:rounded-3xl"
          :src="
            data?.article!.image?.responsiveImage
              ? data?.article!.image?.responsiveImage?.src
              : data?.article!.image?.url
          "
          :srcset="
            data?.article!.image?.responsiveImage
              ? data?.article!.image?.responsiveImage?.webpSrcSet
              : undefined
          "
          :width="data?.article!.image?.width"
          :height="data?.article!.image?.height"
          :alt="data?.article!.image?.alt!"
          sizes="(min-width: 1400px) 1280px, 93.7vw"
        />
      </div>
      <div
        class="relative z-2 col-span-6 -mt-8 rounded-3xl bg-white lg:col-start-2 lg:col-end-12 lg:-mt-44"
      >
        <header class="rounded-t-3xl bg-light-gray-200 px-5 pb-6 pt-5 lg:px-28 lg:pb-9 lg:pt-16">
          <nav class="mb-3">
            <NuxtLink
              v-for="cat in data?.article!.category"
              :key="cat.id"
              class="mr-2 rounded-[30px] bg-light-gray px-3 py-[6px] text-sm tracking-wide text-blue-950"
              :to="
                localePath({
                  path: `/${paths[locale as LocaleCode][ModelApiKey.Article].path}`,
                  query: { category: cat.name }
                })
              "
              >{{ cat.name }}</NuxtLink
            >
          </nav>
          <Heading :level="1" class="mb-3 text-4xl font-light text-blue-950 lg:text-8xl">{{
            data?.article!.title
          }}</Heading>
          <div class="mb-3 flex flex-col gap-x-8 text-lg text-blue-950 lg:mb-8 lg:flex-row">
            <div v-if="data?.article!.author" class="font-medium">
              {{ $t('by') }}
              {{ data?.article!.author ? data?.article!.author + ',' : null }}
              <span class="capitalize">{{ articleDateFormat }}</span>
            </div>
            <div v-if="data?.article!.readTime" class="font-semibold">
              {{ data?.article!.readTime }}
            </div>
          </div>
          <div v-if="data?.article!.excerpt" v-html="data?.article!.excerpt" />
        </header>
        <div class="px-5 py-4 lg:px-28 lg:py-6">
          <StructuredText class="text-blue-950" :data="data?.article!.body" />
        </div>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import { toHead } from 'vue-datocms'
import { useAppStore } from '~/stores/AppStore'
import { paths } from '~/lib/paths'
import { ModelApiKey } from '~/types/models'
import type { StringMultiLocaleField, BooleanTypeMultiLocaleField } from '~/types/generated'

const { locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const store = useAppStore()
const config = useRuntimeConfig()

const { throwError } = useNuxtError()

const { data, error } = await useAsyncGql('article', {
  locale,
  slug: route.params.slug as string,
  options: {
    initialCache: false
  }
})

if (!data?.value!.article || error.value) {
  throwError(404)
}

if (data?.value!.article) {
  route.meta.nuxtI18n = useLocaleSlugs(
    data.value.article._allSlugLocales as StringMultiLocaleField[],
    data.value.article._allPublishTranslationLocales as BooleanTypeMultiLocaleField[]
  )
}

const datoHead = toHead((data.value as any).article.seo)
const i18nHeadLinks = useLocaleHeadLinks(ModelApiKey.Article)

useHead(() => {
  const metaTags = [...(datoHead.meta || [])]

  // Check for noindex
  if ((data.value as any).article.noIndex) {
    metaTags.push({ name: 'robots', hid: 'robots', content: 'noindex' })
  }

  return {
    title: datoHead.title,
    link: [...i18nHeadLinks],
    meta: [
      {
        hid: 'og:url',
        property: 'og:url',
        content: config.public.appUrl + route.fullPath
      },
      ...metaTags
    ]
  }
})

// Get RelatedArticles

const relatedArticles = ref([])

const filterAny = data?.value.article!.category.length
  ? data.value.article!.category.map((x) => x.id)
  : []

const { data: articles } = await useAsyncGql('articles', {
  locale,
  first: 3,
  anyIn: filterAny,
  neq: data?.value.article!.slug,
  options: {
    initialCache: false
  }
})

relatedArticles.value = articles.value.allArticles

const { articleDateFormat } = useDate(data?.value.article.createdAt, locale.value)

onMounted(() => {
  store.setTheme('neutral')
})
</script>
